import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Tooltip } from 'react-tooltip'
import { accessToken } from '@/shared/helpers/CSRF'
import useCart from '@/public/hooks/use-cart'
import useInvoicedSupplement from '@/public/hooks/use-invoiced-supplement'
import getContractPricing from '@/public/helpers/get-contract-pricing'
import { MEMBERSHIP_DISCOUNT_MULTIPLIER, MEMBERSHIP_DISCOUNT_CATEGORIES } from '@/shared/helpers/pricing'
import axios from '../../../lib/httpClient'
import { numberToCurrency } from '../../shared/helpers/String'
import RecommendationActions from './RecommendationActions'

// Map, not a plain object: a plain-object lookup would resolve inherited keys
// like 'toString' to a truthy function and render it as a coverage label.
const COVERAGE_LABELS = new Map([
  ['membership', 'Included in membership'],
  ['employer', 'Covered by employer'],
])

function Recommendation({
  recommendation: {
    id, available_on: availableOn, recommended_for: recommendedFor, product, perpetual,
    decline_ids: declineIds,
  },
  removeRecommendation,
  showMemberPricing,
  cart,
  contractPricingData,
  virtualVisitCoverage,
  tooltipId,
}) {
  const isInvoicedSupplement = useInvoicedSupplement(product, cart)
  const contractPricing = getContractPricing(product, contractPricingData)
  const current = new Date() > new Date(availableOn) || perpetual
  const availableOnMessage = current ? 'Available' : 'Recommended Order Date'
  const recommendedMessage = 'Recommended On'
  const availability = current ? 'success' : 'warning'

  // A lab card entry can stand for more than one recommendation of the same
  // test, and declining it has to close every one or the test comes back.
  function handleDecline() {
    Promise.all((declineIds || [id]).map((declineId) => axios({
      method: 'patch',
      url: `/api/product_recommendations/${declineId}/decline`,
      headers: { 'X-CSRF-Token': accessToken },
    })))
      .then(() => removeRecommendation(id))
  }

  const hasMemberDiscount = showMemberPricing
    && MEMBERSHIP_DISCOUNT_CATEGORIES.includes(product.category)

  const renderPrice = () => {
    // Coverage is absolute, so it short-circuits ahead of invoicing and
    // contract pricing -- the same order products/_price_block_virtual_visit uses.
    // Look the label up rather than defaulting into one: an unrecognized value
    // must fall through to real pricing, never render $0 under a guessed label.
    const coverageLabel = COVERAGE_LABELS.get(virtualVisitCoverage)
    if (coverageLabel) {
      return (
        <>
          <strong>{numberToCurrency(0)}</strong>
          <span className="fw-light fs-6 text-success ms-2">
            {coverageLabel}
          </span>
        </>
      )
    }

    if (isInvoicedSupplement) {
      return (
        <>
          <strong>$0.00</strong>
          <span className="fw-light fs-6 text-success ms-2">
            Covered by employer
          </span>
        </>
      )
    }

    if (contractPricing.isDiscounted && hasMemberDiscount) {
      const memberPrice = contractPricing.discountedPrice * MEMBERSHIP_DISCOUNT_MULTIPLIER
      return (
        <>
          <span className="text-decoration-line-through mx-2">{numberToCurrency(product.price)}</span>
          &nbsp;
          <strong>
            {numberToCurrency(memberPrice)}
            {' '}
            {contractPricing.discountPercentage
              ? `${contractPricing.discountPercentage}% employer discount + member price`
              : 'Employer discount + member price'}
          </strong>
        </>
      )
    }

    if (contractPricing.isDiscounted) {
      return (
        <>
          <span className="text-decoration-line-through mx-2">{numberToCurrency(product.price)}</span>
          &nbsp;
          <strong>
            {numberToCurrency(contractPricing.discountedPrice)}
            {' '}
            {contractPricing.discountPercentage
              ? `${contractPricing.discountPercentage}% employer discount`
              : 'Employer discount'}
          </strong>
        </>
      )
    }

    if (hasMemberDiscount) {
      return (
        <>
          <span className="text-decoration-line-through mx-2">{numberToCurrency(product.price)}</span>
          &nbsp;
          <strong>
            {numberToCurrency(parseFloat(product.price) * MEMBERSHIP_DISCOUNT_MULTIPLIER)}
            {' '}
            member price
          </strong>
        </>
      )
    }

    return <strong>{numberToCurrency(product.price)}</strong>
  }

  return (
    <div className="mb-4 d-flex flex-column flex-sm-row flex-wrap align-items-sm-center justify-content-sm-between gap-2">
      <div>
        <div>
          {product.service_category ? (
            <span
              className="me-2"
              data-tooltip-id={tooltipId}
              data-tooltip-content={product.service_category?.note || ''}
            >
              {product.name}
              &nbsp;
              <span className="info-icon">&#9432;</span>
            </span>
          ) : (
            <span className="me-2">{product.name}</span>
          )}
          {renderPrice()}
        </div>
        <small className={`text-${availability} mt-1`}>
          {perpetual ? recommendedMessage : availableOnMessage}
          {!current || perpetual ? (
            <>
              {' - '}
              {availableOn}
            </>
          ) : null}
        </small>
        {recommendedFor ? (
          <small className="text-muted mt-1 d-block">
            {`Recommended for around ${recommendedFor}`}
          </small>
        ) : null}
      </div>
      <div id={`product-button-${id}`} className="d-flex align-items-center gap-2">
        <RecommendationActions product={product} />
        {perpetual === false && (
          <button type="button" className="btn btn-secondary" onClick={handleDecline}>Decline</button>
        )}
      </div>
    </div>
  )
}

export default function Recommendations({
  recommendations, title, showMemberPricing, supplementInvoicing, contractPricingData,
  virtualVisitCoverage,
}) {
  const tooltipId = `service-category-tooltip-${title.replace(/\s+/g, '-').toLowerCase()}`
  const [recs, setRecs] = useState(recommendations)
  const { cart } = useCart()

  // Use cart data if available, otherwise create cart-like object from server-provided data
  // Semantics: null = not available, [] = all covered, [ids] = only these covered
  const invoicingData = cart || (supplementInvoicing ? {
    supplement_invoicing_eligible_product_ids: supplementInvoicing.eligible_product_ids,
    supplement_invoicing_requires_recommendation: supplementInvoicing.requires_recommendation,
  } : null)

  const removeRecommendation = (id) => {
    setRecs(recs.filter((rec) => rec.id !== id))
  }

  return (
    <div className="card">
      <div className="card-header">
        <h4>{title}</h4>
      </div>
      <div className="card-content">
        {recs.length === 0 && (
          <div>No recommendations yet</div>
        )}
        {recs.map((recommendation) => (
          <Recommendation
            key={recommendation.id}
            recommendation={recommendation}
            removeRecommendation={removeRecommendation}
            showMemberPricing={showMemberPricing}
            cart={invoicingData}
            contractPricingData={contractPricingData}
            virtualVisitCoverage={virtualVisitCoverage}
            tooltipId={tooltipId}
          />
        ))}
        <Tooltip id={tooltipId} />
      </div>
    </div>
  )
}

const contractPricingDataShape = PropTypes.shape({
  discounts: PropTypes.objectOf(
    PropTypes.shape({
      amount: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  category_map: PropTypes.objectOf(PropTypes.string),
})

Recommendation.propTypes = {
  recommendation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    perpetual: PropTypes.bool.isRequired,
    product: PropTypes.shape({
      id: PropTypes.number.isRequired,
      price: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      category: PropTypes.string,
      sku: PropTypes.string,
      is_subscribable: PropTypes.bool.isRequired,
      quantifiable: PropTypes.bool.isRequired,
      service_category: PropTypes.shape({
        note: PropTypes.string,
      }),
    }),
    available_on: PropTypes.string.isRequired,
    recommended_for: PropTypes.string,
    decline_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  removeRecommendation: PropTypes.func.isRequired,
  showMemberPricing: PropTypes.bool,
  cart: PropTypes.shape({
    // Semantics: null = not available, [] = all covered, [ids] = only these covered
    supplement_invoicing_eligible_product_ids: PropTypes.arrayOf(PropTypes.number),
    supplement_invoicing_requires_recommendation: PropTypes.bool,
  }),
  contractPricingData: contractPricingDataShape,
  virtualVisitCoverage: PropTypes.oneOf(['membership', 'employer']),
  tooltipId: PropTypes.string.isRequired,
}

Recommendation.defaultProps = {
  showMemberPricing: false,
  cart: null,
  contractPricingData: null,
  virtualVisitCoverage: null,
}

Recommendations.propTypes = {
  recommendations: PropTypes.arrayOf(PropTypes.shape).isRequired,
  title: PropTypes.string.isRequired,
  showMemberPricing: PropTypes.bool,
  supplementInvoicing: PropTypes.shape({
    // Semantics: null = not available, [] = all covered, [ids] = only these covered
    eligible_product_ids: PropTypes.arrayOf(PropTypes.number),
    requires_recommendation: PropTypes.bool,
  }),
  contractPricingData: contractPricingDataShape,
  virtualVisitCoverage: PropTypes.oneOf(['membership', 'employer']),
}

Recommendations.defaultProps = {
  showMemberPricing: false,
  supplementInvoicing: null,
  contractPricingData: null,
  virtualVisitCoverage: null,
}

